import type { CSSProperties, ReactElement, ReactNode } from "react";

import { Navbar, NavbarGroup, NavbarDivider, Alignment } from "@blueprintjs/core";

import { NavbarSection } from "./navbar-section";

interface AppHeaderProps {
  /** App brand icon shown in the left identity cell (Blueprint Icon, inline SVG, etc.). */
  appIcon: ReactElement;
  /** Accessible label for the app icon button (e.g., "IronCase home"). */
  appIconAriaLabel: string;
  /** Click handler for the app icon — typically navigates to app home. */
  onAppHomeClick: () => void;
  /** Name of the current module/section (Foundry vocab — analogous to a page title). */
  moduleName: string;
  /** Optional breadcrumb trail showing depth within the module. */
  breadcrumbs?: ReactNode;
  /** Optional center slot — typically search/omnibar. */
  center?: ReactNode;
  /** Optional right-side actions (buttons, toggles, dropdowns). */
  actions?: ReactNode;
  /** Custom background color override (CSS color string). Defaults to theme --bp-header-bg. */
  backgroundColor?: string;
  /** Header height (CSS length). Defaults to 50px via .bp-app-header min-height. */
  height?: CSSProperties["height"];
  /** Header orientation. "horizontal" (default) renders left/center/right zones. "vertical" is reserved for future use. */
  orientation?: "horizontal" | "vertical";
}

/**
 * Foundry-style app header — left identity NavbarSection (icon-tab + module
 * name + optional breadcrumbs), optional center slot, optional right action
 * zone. Modeled after Foundry's `ws-app-header` configuration: App + Module
 * are the two identity layers, composed as a NavbarSection.
 */
export function AppHeader({
  appIcon,
  appIconAriaLabel,
  onAppHomeClick,
  moduleName,
  breadcrumbs,
  center,
  actions,
  backgroundColor,
  height,
  orientation = "horizontal",
}: AppHeaderProps) {
  const style: CSSProperties = {};
  if (backgroundColor) style.backgroundColor = backgroundColor;
  if (height !== undefined) style.minHeight = height;

  return (
    <Navbar
      className="bp-app-header"
      data-testid="app-header"
      data-orientation={orientation}
      style={Object.keys(style).length ? style : undefined}
    >
      <NavbarGroup align={Alignment.START}>
        <NavbarSection
          icon={appIcon}
          iconAriaLabel={appIconAriaLabel}
          onIconClick={onAppHomeClick}
          title={moduleName}
        />
        {breadcrumbs && (
          <>
            <NavbarDivider />
            <div className="bp-app-header-breadcrumbs">{breadcrumbs}</div>
          </>
        )}
      </NavbarGroup>
      {center && <div className="bp-app-header-center">{center}</div>}
      {actions && <NavbarGroup align={Alignment.END}>{actions}</NavbarGroup>}
    </Navbar>
  );
}
